import React from 'react';
import Row from 'react-bootstrap/Row';
import classnames from 'classnames';

import Button from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';

import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';

import './style.scss';

export interface ISSOModalProps {
  show: boolean;
  onClose: (e: React.SyntheticEvent<any>) => void;
  onSubmit: (ssoRequest: ISSORequest) => void;
  sso: ISSOResponse;
}

interface ISSOModalState {
  sso: ISSORequest;
  error: {
    label: IFieldValidation;
    clientAppId: IFieldValidation;
    clientSecretKey: IFieldValidation;
  };
}

const cyberdyneValidator = new CyberdyneValidator();
const errorMsgGen = new ErrorMessageGenerator();

class SSOModal extends React.Component<ISSOModalProps, ISSOModalState> {
  constructor(props: ISSOModalProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  getEmptyState = (): ISSOModalState => ({
    sso: {
      isActive: true,
      label: '',
      clientAppId: '',
      clientSecretKey: '',
      submissionOption: SSOSubmissionOption.MAGIC_LINK_AND_THIRD_PARTY_LOGIN,
    },
    error: {
      label: {
        errorMessage: '',
        errorState: null,
      },
      clientAppId: {
        errorMessage: '',
        errorState: null,
      },
      clientSecretKey: {
        errorMessage: '',
        errorState: null,
      },
    },
  });

  componentDidUpdate(prevProps: ISSOModalProps) {
    const hasOpened = this.props.show !== prevProps.show && this.props.show;
    const isEdit = !!this.props.sso;

    if (hasOpened) {
      if (isEdit) {
        this.setState({
          sso: this.props.sso,
          error: this.getEmptyState().error,
        });
      } else {
        this.setState(this.getEmptyState());
      }
    }
  }

  onSubmit = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    if (this.isValid()) {
      this.props.onSubmit(this.getRequestFromState());
    }
  };

  getRequestFromState = (): ISSORequest => {
    const { isActive, label, clientAppId, clientSecretKey, submissionOption } = this.state.sso;

    return {
      isActive,
      label,
      clientAppId,
      clientSecretKey,
      submissionOption,
    };
  };

  handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    this.setState((prevState) => ({
      ...prevState,
      sso: {
        ...prevState.sso,
        [name]: value,
      },
    }));
  };

  isValid = () => {
    const { sso } = this.state;
    const error = this.getEmptyState().error;

    let isValid = true;

    if (cyberdyneValidator.isEmptyString(sso.label)) {
      error.label.errorState = IValidationState.ERROR;
      error.label.errorMessage = errorMsgGen.getMandatoryFieldMessage('Integration Name');

      isValid = false;
    } else if (sso.label.length > 500) {
      error.label.errorState = IValidationState.ERROR;
      error.label.errorMessage = errorMsgGen.getCharacterLimitMessage('Integration Name', 500);

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyString(sso.clientAppId)) {
      error.clientAppId.errorState = IValidationState.ERROR;
      error.clientAppId.errorMessage = errorMsgGen.getMandatoryFieldMessage('Client App ID');

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(sso.clientAppId, 500)) {
      error.clientAppId.errorState = IValidationState.ERROR;
      error.clientAppId.errorMessage = errorMsgGen.getCharacterLimitMessage('Client App ID', 500);

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyString(sso.clientSecretKey)) {
      error.clientSecretKey.errorState = IValidationState.ERROR;
      error.clientSecretKey.errorMessage = errorMsgGen.getMandatoryFieldMessage('Client Secret Key');

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(sso.clientSecretKey, 500)) {
      error.clientSecretKey.errorState = IValidationState.ERROR;
      error.clientSecretKey.errorMessage = errorMsgGen.getCharacterLimitMessage('Client Secret Key', 500);

      isValid = false;
    }

    this.setState({
      error,
    });

    return isValid;
  };

  renderTitle = () =>
    this.props.sso ? 'Edit CRM 3rd Party SSO Integration' : 'Add & Configure CRM 3rd Party SSO Integration';

  renderBody = () => {
    const ssoStatusOptions = [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' },
    ];

    const ssoSubmissionOptions = [
      { value: SSOSubmissionOption.MAGIC_LINK_AND_THIRD_PARTY_LOGIN, label: 'Magic Link & 3rd Party Login' },
      { value: SSOSubmissionOption.MAGIC_LINK, label: 'Magic Link' },
      { value: SSOSubmissionOption.THIRD_PARTY_LOGIN, label: '3rd Party Login' },
    ];

    const { sso, error } = this.state;

    return (
      <div className={classnames('integration__body')}>
        <Row>
          <Input
            width={4}
            field={{
              label: 'Status',
              type: OtherFieldType.RADIOBUTTON,
              value: sso.isActive,
              isRequired: true,
            }}
            name="isActive"
            valueLabelOptions={ssoStatusOptions}
            onChange={this.handleChange}
          />
        </Row>
        <Row>
          <Input
            width={4}
            field={{
              label: 'Integration Name',
              type: CustomFieldType.TEXT,
              value: sso.label,
              isRequired: true,
            }}
            name="label"
            onChange={this.handleChange}
            error={error.label}
          />
        </Row>
        <Row>
          <Input
            field={{
              label: 'Client App ID',
              type: CustomFieldType.TEXT,
              isRequired: true,
              value: sso.clientAppId || '',
            }}
            error={error.clientAppId}
            width={4}
            name="clientAppId"
            onChange={this.handleChange}
          />
          <Input
            field={{
              label: 'Client Secret Key',
              type: CustomFieldType.TEXT,
              isRequired: true,
              value: sso.clientSecretKey || '',
            }}
            error={error.clientSecretKey}
            width={4}
            name="clientSecretKey"
            onChange={this.handleChange}
          />
        </Row>
        <Row>
          <Input
            field={{
              label: 'View Submissions Options',
              type: CustomFieldType.PICKLIST,
              value: sso.submissionOption,
              options: ssoSubmissionOptions,
              isRequired: true,
            }}
            width={4}
            name="submissionOption"
            onChange={this.handleChange}
          />
        </Row>
      </div>
    );
  };

  renderFooter = () => {
    const submitBtnTxt = this.props.sso ? 'Save' : 'Add Integration';

    return (
      <div className={classnames('integration__footer')}>
        <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.props.onClose} content="Cancel" />
        <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onSubmit} content={submitBtnTxt} />
      </div>
    );
  };

  render() {
    return (
      <ModalBase
        show={this.props.show}
        onClose={this.props.onClose}
        titleElement={this.renderTitle()}
        bodyElement={this.renderBody()}
        footerElement={this.renderFooter()}
        className={classnames('integration-modal')}
      />
    );
  }
}

export default SSOModal;
